import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useFetcher } from '@remix-run/react'
import React from 'react'
import { z } from 'zod'
import { type action } from '#app/routes/products+/$categorySlug.$productId.$productSlug.report-review.tsx'
import { useIsPending } from '#app/utils/misc.tsx'
import { Button } from './ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { Icon } from './ui/icon'
import { StatusButton } from './ui/status-button'

export const ReportReviewFormSchema = z.object({
	reviewId: z.string(),
})

type Props = {
	reviewId: string
}

export const ReportReviewForm: React.FC<Props> = ({ reviewId }) => {
	const fetcher = useFetcher<typeof action>()
	const isPending = useIsPending()
	const [form, fields] = useForm({
		id: `report-review-${reviewId}`,
		constraint: getZodConstraint(ReportReviewFormSchema),
		lastResult: fetcher.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ReportReviewFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant={'link'} aria-label="Open report review menu">
					<Icon name="dots-vertical" size="font" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					<DropdownMenuItem asChild>
						<fetcher.Form
							method="POST"
							action="report-review"
							preventScrollReset={true}
							{...getFormProps(form)}
						>
							<input
								{...getInputProps(fields.reviewId, { type: 'hidden' })}
								value={reviewId}
							/>
							<StatusButton
								variant={'ghost'}
								type="submit"
								status={isPending ? 'pending' : (form.status ?? 'idle')}
								className="p-0"
							>
								<Icon name={'drawing-pin'}>Report review</Icon>
							</StatusButton>
						</fetcher.Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}
